import { useState } from "react";
import { X, Plus } from "lucide-react";

/**
 * Reusable chip-style tag input.
 * - value: string[]
 * - onChange: (string[]) => void
 * - suggestions: string[] (optional)
 * - placeholder, testId, disabled
 */
export default function TagInput({
  value = [],
  onChange,
  suggestions = [],
  placeholder = "הוסף תיוג ולחץ Enter…",
  testId = "tag-input",
  disabled = false,
  compact = false,
}) {
  const [draft, setDraft] = useState("");

  const normalize = (t) =>
    (t || "").trim().toLowerCase().replace(/\s+/g, "_").replace(/[^\w\-\u0590-\u05ff]/g, "").slice(0, 40);

  const addTag = (raw) => {
    const t = normalize(raw);
    if (!t) return;
    if (value.includes(t)) {
      setDraft("");
      return;
    }
    onChange([...value, t]);
    setDraft("");
  };

  const removeTag = (t) => onChange(value.filter((v) => v !== t));

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (draft.trim()) addTag(draft);
    } else if (e.key === "Backspace" && !draft && value.length > 0) {
      removeTag(value[value.length - 1]);
    }
  };

  const filteredSug = suggestions
    .filter((s) => !value.includes(s))
    .filter((s) => (draft ? s.includes(normalize(draft)) : true))
    .slice(0, 6);

  return (
    <div className="w-full" data-testid={testId}>
      <div className={`flex flex-wrap items-center gap-1.5 ${compact ? "px-2 py-1.5" : "px-3 py-2"} rounded-xl border border-stone-300 bg-white focus-within:border-brand-amber focus-within:ring-1 focus-within:ring-brand-amber/40 transition`}>
        {value.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-brand-amber text-xs font-bold px-2 py-0.5"
            data-testid={`tag-chip-${t}`}
          >
            {t}
            {!disabled && (
              <button
                type="button"
                onClick={() => removeTag(t)}
                className="hover:bg-amber-100 rounded-full p-0.5"
                aria-label={`הסר ${t}`}
                data-testid={`tag-remove-${t}`}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={() => draft.trim() && addTag(draft)}
            placeholder={value.length === 0 ? placeholder : ""}
            className={`flex-1 min-w-[80px] outline-none bg-transparent ${compact ? "text-xs py-0.5" : "text-sm py-1"}`}
            data-testid={`${testId}-input`}
          />
        )}
      </div>
      {filteredSug.length > 0 && !disabled && (
        <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`${testId}-suggestions`}>
          {filteredSug.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => addTag(s)}
              className="inline-flex items-center gap-1 rounded-full border border-stone-200 hover:border-brand-amber hover:bg-amber-50 text-stone-600 text-xs px-2 py-0.5 transition"
              data-testid={`tag-suggest-${s}`}
            >
              <Plus className="w-3 h-3" />
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
